/**
 * Baked melee hit-arc evaluation + the swept test — the melee analogue of what
 * `projectiles.ts` does for bolts, sharing the same `hit.ts` primitives.
 *
 * A HitArc is the weapon's real, BAKED path through an attack, in CHARACTER-LOCAL
 * space (see client/test/bake-hitvolumes.mts). Client prediction and server authority
 * both evaluate this one structure, so they cannot disagree.
 *
 * The sweep is the whole point: testing the blade at its instantaneous pose lets a fast
 * swing pass clean through a target between ticks. We sweep the segment the blade
 * TRAVELLED over the tick, exactly as bolts sweep their flight segment.
 */
import { segmentCapsule } from "./hit.js";

export interface HitArcSample {
  /** Normalized time within the action's ACTIVE window, 0..1. */
  t: number;
  /** Blade endpoints in CHARACTER-LOCAL space (metres). */
  base: readonly [number, number, number];
  tip: readonly [number, number, number];
}

export interface HitArc {
  /** Blade thickness (m) — the swept capsule's radius. */
  radius: number;
  /** Ascending by `t`. At least 2 samples. */
  samples: readonly HitArcSample[];
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Blade endpoints at normalized active-window time `t`, linearly interpolated between
 * the two straddling samples. Writes [baseX,baseY,baseZ, tipX,tipY,tipZ] into `out`
 * (allocation-free — `out` is caller-owned scratch of length >= 6).
 */
export function evalArc(arc: HitArc, t: number, out: number[]): void {
  const s = arc.samples;
  const u = clamp01(t);
  // Degenerate arcs (bad/truncated bake data) must not throw — this runs server-side on
  // data loaded from a file. 0 samples -> the origin pose; 1 sample -> that pose, always.
  if (s.length === 0) {
    out[0] = 0; out[1] = 0; out[2] = 0; out[3] = 0; out[4] = 0; out[5] = 0;
    return;
  }
  if (s.length === 1) {
    const only = s[0];
    out[0] = only.base[0]; out[1] = only.base[1]; out[2] = only.base[2];
    out[3] = only.tip[0]; out[4] = only.tip[1]; out[5] = only.tip[2];
    return;
  }
  let hi = 1;
  while (hi < s.length - 1 && s[hi].t < u) hi++;
  const a = s[hi - 1];
  const b = s[hi];
  const span = b.t - a.t;
  const f = span > 1e-9 ? clamp01((u - a.t) / span) : 0;
  out[0] = a.base[0] + (b.base[0] - a.base[0]) * f;
  out[1] = a.base[1] + (b.base[1] - a.base[1]) * f;
  out[2] = a.base[2] + (b.base[2] - a.base[2]) * f;
  out[3] = a.tip[0] + (b.tip[0] - a.tip[0]) * f;
  out[4] = a.tip[1] + (b.tip[1] - a.tip[1]) * f;
  out[5] = a.tip[2] + (b.tip[2] - a.tip[2]) * f;
}

const _p0: number[] = [0, 0, 0, 0, 0, 0];
const _p1: number[] = [0, 0, 0, 0, 0, 0];

/**
 * Sweep the blade from active-window time `t0` to `t1` against a capsule (core A->B,
 * radius `r`), all in ONE frame (character-local, or both pre-transformed to the same
 * world/planet-local frame — the caller's choice, but they must match). Scalar args, no
 * allocation — this runs per-tick per-target on both sides (see hit.ts).
 *
 * The blade is itself a segment, so a true segment-swept-against-capsule is a
 * quadrilateral-vs-capsule test. We approximate the way every shipping game does: test the
 * two RAILS the blade's endpoints trace (base and tip) plus the blade's own segment at the
 * baked sample poses. `arc.radius` covers the gap between the rails, which is why it is
 * generous (0.5 m, inherited from SWORD_BLADE_THICK).
 *
 * Crucially we do NOT chord the whole span: we walk the BAKED SAMPLE INTERVALS that fall
 * inside [t0,t1] and sweep each one. Inside a single interval `evalArc` is linear, so the
 * rail IS the true path and no accuracy is lost; across intervals a chord would cut the
 * corner off a curved swing and lose hits at the outer edge of reach. Cost is bounded by
 * the sample count (12).
 *
 * SUBDIVISION INVARIANCE — the property that makes client prediction and server authority
 * agree. The client steps this per tick (~1/60 s); the server calls it ONCE with (0,1) for
 * the whole swing. Both must reach the same verdict, so everything we test must depend only
 * on the INTERVAL, never on how the caller partitioned it:
 *   - the RAILS are free: a sub-step's rail is a sub-segment of the same straight interval
 *     rail, so the union over any partition is exactly the whole rail;
 *   - the BLADE BODY is tested at the baked SAMPLE poses lying in [t0,t1] and NOWHERE else
 *     — never at t0/t1 for their own sake. The closed sub-intervals of any partition of
 *     [t0,t1] cover precisely that same set of samples, so a fine-stepper tests exactly the
 *     poses the single (0,1) call tests.
 * Agreement is therefore true BY CONSTRUCTION, not by numerical margin — no constraint is
 * imposed on how coarse the bake or how thin the blade may be. (A coarse bake with a thin
 * blade will simply have blind spots between sample poses — but the SAME blind spots on
 * both sides.)
 *
 * The shipping bake has none, and that is PROVED rather than implied by a sample count. Its
 * three arcs are big — the tip travels 220-381 deg through them — so 12 samples alone would
 * guarantee nothing; what does is (a) the samples are spaced by equal TIP TRAVEL, not equal
 * time, so they cluster where the blade is fastest, and (b) the bake measures, for every
 * emitted interval, how far the TRUE densely-sampled rail bows away from the straight chord
 * this function tests, and FAILS the build if that sagitta exceeds the blade radius. Today it
 * is 0.122 / 0.153 / 0.205 m against a 0.5 m radius. That radius also covers the band between
 * the base and tip rails. See the BLIND-SPOT GUARD in client/test/bake-hitvolumes.mts.
 *
 * Returns -1 for a miss. On a hit it returns an ABSOLUTE active-window time `t` in [t0,t1]
 * (NOT a fraction of the span): the sub-steps are walked in order, and within a sub-step
 * the FIRST of {tip rail, base rail, blade body} to report contact wins — so this is not
 * the earliest contact in time, just a time at which the blade was touching. It exists to
 * place impact FX; nothing authoritative may key off its exact value.
 */
export function sweepArcVsCapsule(
  arc: HitArc,
  t0: number,
  t1: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  r: number,
): number {
  const s = arc.samples;
  const n = s.length;
  if (n === 0) return -1; // degenerate arc: nothing to sweep

  let lo = clamp01(t0);
  let hi = clamp01(t1);
  if (hi < lo) { const tmp = lo; lo = hi; hi = tmp; }
  const rad = r + arc.radius;

  // Scratch aliases so we can carry each sub-step's end pose into the next step's start
  // pose by swapping references — no copying, no allocation.
  let from = _p0;
  let to = _p1;
  evalArc(arc, lo, from);

  // First baked sample strictly after `lo`: the first interior sub-step boundary.
  let i = 0;
  while (i < n && s[i].t <= lo) i++;

  // The blade's own body at the START pose — but ONLY when `lo` is itself a baked sample
  // time (the usual case for the server's (0,1) call, where lo == samples[0].t). Testing it
  // because it is a SAMPLE, not because it is this caller's step boundary, is what keeps the
  // pose set a function of the interval alone: a fine-stepper's tick boundaries land between
  // samples and so add nothing here. Hoisted, so the loop below never retests it.
  if (i > 0 && s[i - 1].t === lo) {
    const startBladeHit = segmentCapsule(
      from[0], from[1], from[2], from[3], from[4], from[5],
      ax, ay, az, bx, by, bz, rad,
    );
    if (startBladeHit >= 0) return lo;
  }

  let segStart = lo;
  for (;;) {
    // A sub-step runs to the next baked sample, or to `hi`, whichever comes first. When it
    // ends ON a sample, that sample's pose is a blade-body test site; when it ends at `hi`
    // between samples, it is not.
    const atSample = i < n && s[i].t <= hi;
    const segEnd = atSample ? s[i].t : hi;
    evalArc(arc, segEnd, to);
    const span = segEnd - segStart;

    // Rail 1: the TIP's path over this sub-step — the fastest-moving part, the tunneller.
    const tipHit = segmentCapsule(
      from[3], from[4], from[5], to[3], to[4], to[5],
      ax, ay, az, bx, by, bz, rad,
    );
    if (tipHit >= 0) return segStart + span * tipHit;

    // Rail 2: the BASE's path (a target hugging the hilt).
    const baseHit = segmentCapsule(
      from[0], from[1], from[2], to[0], to[1], to[2],
      ax, ay, az, bx, by, bz, rad,
    );
    if (baseHit >= 0) return segStart + span * baseHit;

    // The blade's own body at this SAMPLE pose (a target the blade is lying across, too far
    // from either rail for the rails to see). Sample poses only — see SUBDIVISION INVARIANCE.
    if (atSample) {
      const bladeHit = segmentCapsule(
        to[0], to[1], to[2], to[3], to[4], to[5],
        ax, ay, az, bx, by, bz, rad,
      );
      if (bladeHit >= 0) return segEnd;
    }

    if (segEnd >= hi) return -1;
    const tmp = from; from = to; to = tmp; // this step's end pose is the next step's start
    segStart = segEnd;
    i++;
  }
}
